"use client";

import { useState } from "react";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { CourseShowcaseSectionHeader } from "./CourseShowcaseSectionHeader";

type Props = {
  items: string[];
  eyebrow: string;
  title: string;
  /** Pre-interpolated with the item count by the server (locale-aware). */
  showAllLabel: string;
  showLessLabel: string;
};

/**
 * Sits directly under the hero, so a well-sized outcome list must render in
 * full — collapsing only kicks in for longer, legacy lists.
 */
const INITIAL_VISIBLE = 10;

export function WhatYouWillLearnSection({
  items,
  eyebrow,
  title,
  showAllLabel,
  showLessLabel,
}: Props) {
  const [showAll, setShowAll] = useState(false);

  const cleaned = items.map((s) => s.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;

  const hasMore = cleaned.length > INITIAL_VISIBLE;
  const visibleItems = showAll ? cleaned : cleaned.slice(0, INITIAL_VISIBLE);

  return (
    <section id="course-what-you-learn" className="scroll-mt-28">
      <CourseShowcaseSectionHeader eyebrow={eyebrow} title={title} size="lg" />
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-7">
        <ul className="grid gap-3 sm:grid-cols-2">
          {visibleItems.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle2 aria-hidden className="mt-0.5 h-[18px] w-[18px] flex-shrink-0 text-emerald-700" />
              <span className="text-[15px] leading-[1.6] text-neutral-700">{item}</span>
            </li>
          ))}
        </ul>

        {hasMore && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="mt-5 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            {showAll ? showLessLabel : showAllLabel}
            <ChevronDown aria-hidden
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                showAll && "rotate-180",
              )}
            />
          </button>
        )}
      </div>
    </section>
  );
}
